import { useCallback, useState } from "react";
import { toast } from "sonner";

import { useCreateRoleExplanationMutation } from "@/app/Views/FitCheck/config/api.js";

import { isValidRoleExplanations, JobDetails, RoleExplanations } from "../utils/helpers";

interface UseRoleExplainerReturn {
	roleExplanations: RoleExplanations | null;
	explainerIsLoading: boolean;
	handleRoleExplainer: () => void;
}

/**
 * Hook to manage role explainer API calls and state
 * @param jobDetails - The current job details
 * @returns Role explainer state and handler
 */
export function useRoleExplainer(jobDetails: JobDetails): UseRoleExplainerReturn {
	const [explainerIsLoading, setExplainerIsLoading] = useState(false);
	const [roleExplanations, setRoleExplanations] = useState<RoleExplanations | null>(null);
	const { mutate: createRoleExplainer } = useCreateRoleExplanationMutation();

	const handleRoleExplainer = useCallback(() => {
		setExplainerIsLoading(true);
		createRoleExplainer(
			{
				jobDetails: jobDetails as unknown as Record<string, unknown>,
				jobId: jobDetails._id || "",
				userId: "", // Not used by the API but required by type
			},
			{
				onSuccess: (response) => {
					// API returns results with RoleExplanations shape, but TypeScript types are mismatched
					const results = (response as unknown as { results?: unknown })?.results ?? null;
					if (isValidRoleExplanations(results)) {
						setRoleExplanations(results);
					} else {
						setRoleExplanations(null);
						toast.warning("Role Explainer is not available, Try again later.");
					}
					setExplainerIsLoading(false);
				},
				onError: () => {
					setExplainerIsLoading(false);
					toast.error("Failed to run Role Explainer. Please try again.");
				},
			},
		);
	}, [createRoleExplainer, jobDetails]);

	return {
		roleExplanations,
		explainerIsLoading,
		handleRoleExplainer,
	};
}
